import React, { useState } from 'react';
import { useUnifiedAuth } from '../../../hooks/useUnifiedAuth';

const DoctorProfileCard = ({ doctor, searchQuery }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const { user } = useUnifiedAuth();
  const isAdmin = user?.user_type === 'admin';

  const handleToggleListing = async () => {
    const isCurrentlyListed = doctor.is_listed !== false;
    const action = isCurrentlyListed ? 'remove from website' : 'add to website';
    
    if (!window.confirm(`Are you sure you want to ${action} this doctor?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('sessionToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/edit-user/${doctor.user_id || doctor.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_listed: !isCurrentlyListed })
      });

      if (response.ok) {
        alert(`✅ Successfully ${!isCurrentlyListed ? 'listed' : 'unlisted'} doctor`);
        window.location.reload(); // Refresh to show changes
      } else {
        const errorData = await response.json();
        alert(`❌ Failed: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error toggling listing:', error);
      alert('❌ Error updating listing status');
    }
  };

  // Highlight search terms in text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  // Format consultation fee
  const formatFee = (fee) => {
    return fee ? `$${fee}` : 'Contact for pricing';
  };

  // Get availability status
  const getAvailabilityStatus = (doctor) => {
    // Mock availability logic - in real app, this would check actual schedules
    const statuses = ['Available Today', 'Available Tomorrow', 'Available This Week', 'Booking Required'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  // Render rating stars
  const renderRating = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => (
            <svg
              key={star}
              className={`w-4 h-4 ${
                star <= rating ? 'text-yellow-400' : 'text-gray-300'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>
        <span className="text-sm text-gray-600">
          {rating} ({doctor.total_reviews || 0} reviews)
        </span>
      </div>
    );
  };

  const availabilityStatus = getAvailabilityStatus(doctor);

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="p-6">
        {/* Header Section */}
        <div className="flex items-start space-x-4 mb-4">
          {/* Profile Image */}
          <div className="flex-shrink-0">
            <img
              src={doctor.profile_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor.name)}&background=3B82F6&color=fff&size=64`}
              alt={doctor.name}
              className="w-16 h-16 rounded-full border-2 border-gray-200 object-cover"
            />
            {doctor.is_verified && (
              <div className="relative -mt-2 -mr-2 float-right">
                <div className="bg-green-500 rounded-full p-1">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Doctor Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {highlightText(doctor.name, searchQuery)}
                </h3>
                <p className="text-blue-600 font-medium">
                  {highlightText(doctor.speciality, searchQuery)}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {doctor.qualification}
                </p>
              </div>
              
              {/* Consultation Fee */}
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  {formatFee(doctor.consultation_fee)}
                </div>
                <div className="text-xs text-gray-500">Consultation</div>
              </div>
            </div>
          </div>
        </div>

        {/* Rating and Experience */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {renderRating(doctor.rating || 4.5)}
            <div className="text-sm text-gray-600">
              <span className="font-medium">{doctor.experience_years || 0}</span> years experience
            </div>
          </div>
          
          {/* Availability Badge */}
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            availabilityStatus.includes('Today') 
              ? 'bg-green-100 text-green-800'
              : availabilityStatus.includes('Tomorrow')
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-800'
          }`}>
            {availabilityStatus}
          </div>
        </div>

        {/* Clinic Information */}
        {doctor.clinic_name && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-2">
              <svg className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {highlightText(doctor.clinic_name, searchQuery)}
                </p>
                {doctor.clinic_address && (
                  <p className="text-xs text-gray-600 mt-1">
                    {doctor.clinic_address}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bio/Description */}
        {doctor.bio && (
          <div className="mb-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              {showFullBio ? doctor.bio : `${doctor.bio.substring(0, 150)}...`}
              {doctor.bio.length > 150 && (
                <button
                  onClick={() => setShowFullBio(!showFullBio)}
                  className="text-blue-600 hover:text-blue-700 font-medium ml-1"
                >
                  {showFullBio ? 'Show less' : 'Read more'}
                </button>
              )}
            </p>
          </div>
        )}

        {/* Languages */}
        {doctor.languages && doctor.languages.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Languages:</span>
              <div className="flex flex-wrap gap-1">
                {doctor.languages.map((language, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                  >
                    {language}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium">
            Book Appointment
          </button>
          {/* Admin Controls */}
          {isAdmin && (
            <button 
              onClick={handleToggleListing}
              className={`px-4 py-2 border rounded-lg transition-colors font-medium ${
                doctor.is_listed !== false 
                  ? 'border-orange-300 text-orange-700 hover:bg-orange-50' 
                  : 'border-green-300 text-green-700 hover:bg-green-50'
              }`}
              title={doctor.is_listed !== false ? 'Remove from website' : 'Add to website'}
            >
              {doctor.is_listed !== false ? '🚫 Unlist' : '✅ List'}
            </button>
          )}
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorProfileCard;